/**
 * Gift Activity Page
 * ==================
 * Combined view of all gift transactions with filters.
 * Filters: All | Received | Sent
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Filter, Gift, Send, ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import NearULogo from '../components/NearULogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GiftActivityPage = () => {
  const navigate = useNavigate();
  const [isDark, setIsDark] = useState(true);
  const [filter, setFilter] = useState('all'); // all, received, sent
  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkTheme = () => {
      const saved = localStorage.getItem('nearU_theme');
      setIsDark(saved !== 'light');
    };
    checkTheme();
    const interval = setInterval(checkTheme, 100);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchGifts();
  }, []);

  const fetchGifts = async () => {
    try {
      const res = await fetch(`${API}/gifts/wallet`);
      if (res.ok) {
        const data = await res.json();
        const allGifts = [
          ...(data.gifts_sent || []).map(g => ({ ...g, type: 'sent' })),
          ...(data.gifts_received || []).map(g => ({ ...g, type: 'received' }))
        ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setGifts(allGifts);
      }
    } catch (error) {
      console.error('Error fetching gifts:', error);
    } finally {
      setLoading(false);
    }
  };

  const colors = {
    background: isDark ? '#0D0D0D' : '#FDFCFA',
    cardBg: isDark ? 'rgba(26, 15, 10, 0.95)' : 'rgba(255, 255, 255, 0.98)',
    text: isDark ? '#E6D7C3' : '#1A1410',
    textMuted: isDark ? '#8A7355' : '#4A4035',
    border: isDark ? 'rgba(165, 124, 51, 0.2)' : 'rgba(165, 124, 51, 0.15)',
    accent: '#D4AF37'
  };

  const formatCurrency = (cents) => {
    if (!cents && cents !== 0) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(cents / 100);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getCategoryEmoji = (category) => {
    if (!category) return '🎁';
    if (category.includes('dinner')) return '🍽';
    if (category.includes('drink')) return '🍸';
    if (category.includes('spa')) return '💆';
    if (category.includes('bottle')) return '🍾';
    if (category.includes('shop')) return '🛍';
    if (category.includes('travel')) return '✈️';
    if (category.includes('entertainment')) return '🎟';
    if (category.includes('celebration')) return '🎉';
    return '✨';
  };

  const filteredGifts = gifts.filter(gift => {
    if (filter === 'all') return true;
    return gift.type === filter;
  });

  const filterTabs = [
    { id: 'all', label: 'All', icon: Gift },
    { id: 'received', label: 'Received', icon: ArrowDownLeft },
    { id: 'sent', label: 'Sent', icon: ArrowUpRight }
  ];

  return (
    <div style={{ 
      minHeight: '100vh', 
      paddingBottom: '100px', 
      background: isDark 
        ? 'linear-gradient(180deg, #0D0D0D 0%, #1A0F0A 50%, #0D0D0D 100%)'
        : 'linear-gradient(180deg, #FAFAFA 0%, #F5F5F5 100%)'
    }}>
      {/* Header */}
      <div style={{ 
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
      }}>
        <button
          onClick={() => navigate('/gifts')}
          style={{
            background: 'rgba(138, 115, 85, 0.15)',
            border: 'none',
            borderRadius: '10px',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: colors.text
          }}
        >
          <ChevronLeft size={22} />
        </button>
        
        <h1 style={{ 
          fontFamily: 'Cinzel, serif', 
          fontSize: '22px',
          fontWeight: '600',
          color: colors.text,
          margin: 0,
          flex: 1
        }}>
          Gift Activity
        </h1>
      </div>

      {/* Filter Tabs */}
      <div style={{ padding: '0 16px 20px' }}>
        <div style={{
          display: 'flex',
          background: 'rgba(138, 115, 85, 0.1)',
          borderRadius: '12px',
          padding: '4px',
          gap: '4px'
        }}>
          {filterTabs.map(tab => {
            const Icon = tab.icon;
            const isActive = filter === tab.id;
            const count = tab.id === 'all' 
              ? gifts.length 
              : gifts.filter(g => g.type === tab.id).length;
            
            return (
              <button
                key={tab.id}
                onClick={() => setFilter(tab.id)}
                data-testid={`filter-${tab.id}`}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  borderRadius: '10px',
                  border: 'none',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.2s',
                  ...(isActive ? {
                    background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
                    color: '#2C1B13',
                    boxShadow: '0 2px 8px rgba(212, 175, 55, 0.3)'
                  } : {
                    background: 'transparent',
                    color: colors.textMuted
                  })
                }}
              >
                <Icon size={16} />
                {tab.label}
                <span style={{
                  padding: '2px 6px',
                  borderRadius: '8px',
                  fontSize: '11px',
                  fontWeight: '700',
                  background: isActive ? 'rgba(0,0,0,0.15)' : 'rgba(138, 115, 85, 0.2)'
                }}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Gift List */}
      <div style={{ padding: '0 16px' }}>
        {loading ? (
          <div style={{
            padding: '40px',
            textAlign: 'center',
            color: colors.textMuted
          }}>
            Loading...
          </div>
        ) : filteredGifts.length === 0 ? (
          <div style={{
            background: colors.cardBg,
            borderRadius: '16px',
            border: `1px solid ${colors.border}`,
            padding: '40px 24px',
            textAlign: 'center'
          }}>
            <Gift size={48} color={colors.textMuted} style={{ opacity: 0.4, marginBottom: '16px' }} />
            <p style={{ color: colors.textMuted, fontSize: '15px', margin: 0 }}>
              {filter === 'all' ? 'No gift activity yet' :
               filter === 'received' ? 'No gifts received yet' :
               'No gifts sent yet'}
            </p>
          </div>
        ) : (
          <div style={{
            background: colors.cardBg,
            borderRadius: '16px',
            border: `1px solid ${colors.border}`,
            overflow: 'hidden'
          }}>
            {filteredGifts.map((gift, index) => (
              <div
                key={gift.id || index}
                onClick={() => navigate(`/gift/${gift.id}`)}
                data-testid={`gift-item-${gift.id}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  padding: '16px',
                  cursor: 'pointer',
                  borderBottom: index < filteredGifts.length - 1 ? `1px solid ${colors.border}` : 'none',
                  transition: 'background 0.2s'
                }}
              >
                {/* Category Icon */}
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '14px',
                  background: gift.type === 'sent' 
                    ? 'rgba(33, 150, 243, 0.12)'
                    : 'rgba(76, 175, 80, 0.12)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px',
                  flexShrink: 0
                }}>
                  {getCategoryEmoji(gift.category)}
                </div>
                
                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '4px'
                  }}>
                    <span style={{ 
                      color: colors.text, 
                      fontSize: '15px', 
                      fontWeight: '600',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {gift.type === 'sent' 
                        ? gift.recipient_name || 'Someone'
                        : gift.sender_name || 'Someone'}
                    </span>
                    <span style={{
                      fontSize: '10px',
                      padding: '2px 6px',
                      borderRadius: '6px',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      background: gift.type === 'sent' 
                        ? 'rgba(33, 150, 243, 0.15)'
                        : 'rgba(76, 175, 80, 0.15)',
                      color: gift.type === 'sent' ? '#2196F3' : '#4CAF50'
                    }}>
                      {gift.type === 'sent' ? 'Sent' : 'Received'}
                    </span>
                  </div>
                  <div style={{ 
                    color: colors.textMuted, 
                    fontSize: '13px'
                  }}>
                    {gift.category?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} • {formatDate(gift.created_at)}
                  </div>
                  {gift.message && (
                    <div style={{
                      color: colors.textMuted,
                      fontSize: '12px',
                      marginTop: '4px',
                      fontStyle: 'italic',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      "{gift.message}"
                    </div>
                  )}
                </div>

                {/* Amount & Status */}
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ 
                    color: gift.type === 'sent' ? colors.text : '#4CAF50',
                    fontSize: '17px', 
                    fontWeight: '700',
                    fontFamily: 'Cinzel, serif'
                  }}>
                    {gift.type === 'sent' ? '-' : '+'}{formatCurrency(gift.gross_gift_amount_cents)}
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: gift.state === 'accepted' || gift.state === 'sent' ? '#4CAF50' : 
                           gift.state === 'declined' ? '#F44336' : colors.textMuted,
                    fontWeight: '500',
                    marginTop: '2px'
                  }}>
                    {gift.state?.replace('_', ' ').toUpperCase() || 'SENT'}
                  </div>
                </div>

                <ChevronRight size={18} color={colors.textMuted} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GiftActivityPage;
